import React from 'react';

import { useTranslation } from '@/i18n';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { Selector } from '@/components/ui/inputs/selector';
import { DatePicker } from '@/components/ui/filters/DatePicker';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { Checkbox } from '@/components/ui/inputs/Checkbox';
import { useCreateHolidayFormPanel } from '@/hooks/attendance/holiday/useCreateHolidayFormPanel';


interface CreateHolidayFormPanelProps {
  onSubmit?: () => void;
  readOnly?: boolean;
}

export const CreateHolidayFormPanel: React.FC<CreateHolidayFormPanelProps> = ({
  onSubmit,
  readOnly,
}) => {
  const { t: translate } = useTranslation();

  const {
    holidayTypeValue,
    holidayTypeOptions,
    dateValue,
    reasonValue,
    nonWaivableValue,
    isInhabilesMode,
    fullYearChecked,
    isBulkMode,
    yearValue,
    yearOptions,
    selectedWeekdays,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    errors,
    bulkErrors,
    isSubmitted,
    bulkIsSubmitted,
    handleHolidayTypeChange,
    handleDateChange,
    handleReasonChange,
    handleNonWaivableChange,
    handleFullYearChange,
    handleYearChange,
    handleWeekdayToggle,
    handleSubmit,
  } = useCreateHolidayFormPanel({ onSubmit, readOnly });

  const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="create-holiday-form-panel">
      <SidePanelFormLayout.Fields>
        <Selector
          label={translate('attendance:holiday.form.holidayType')}
          placeholder={translate('attendance:holiday.form.holidayTypePlaceholder')}
          required
          options={holidayTypeOptions}
          value={holidayTypeValue}
          onChange={handleHolidayTypeChange}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.holidayTypeId && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.holidayTypeId?.message && isSubmitted
              ? translate(String(errors.holidayTypeId.message))
              : undefined
          }
          data-testid="holiday-type-selector"
        />

        {isInhabilesMode && (
          <Checkbox
            label={translate('attendance:holiday.form.fullYear')}
            checked={fullYearChecked}
            onChange={handleFullYearChange}
            disabled={effectiveReadOnly || isSubmitting}
            data-testid="holiday-full-year-checkbox"
          />
        )}

        {isBulkMode ? (
          <>
            <Selector
              label={translate('attendance:holiday.form.year')}
              placeholder={translate('attendance:holiday.form.yearPlaceholder')}
              required
              options={yearOptions}
              value={yearValue}
              onChange={handleYearChange}
              disabled={effectiveReadOnly || isSubmitting}
              color={bulkErrors.year && bulkIsSubmitted ? 'error' : 'default'}
              supportingText={
                bulkErrors.year?.message && bulkIsSubmitted
                  ? translate(String(bulkErrors.year.message))
                  : undefined
              }
              data-testid="holiday-year-selector"
            />

            <div className="flex flex-col gap-2" data-testid="holiday-weekdays-group">
              <span className="text-sm font-medium">
                {translate('attendance:holiday.form.weekdays')} *
              </span>
              {WEEKDAYS.map((weekday) => (
                <Checkbox
                  key={weekday}
                  label={translate(`attendance:holiday.form.weekdayLabels.${weekday}`)}
                  checked={selectedWeekdays.includes(weekday)}
                  onChange={(checked) => handleWeekdayToggle(weekday, checked)}
                  disabled={effectiveReadOnly || isSubmitting}
                  data-testid={`holiday-weekday-${weekday}-checkbox`}
                />
              ))}
              {bulkErrors.weekdays?.message && bulkIsSubmitted && (
                <span className="text-sm text-red-500" data-testid="holiday-weekdays-error">
                  {translate(String(bulkErrors.weekdays.message))}
                </span>
              )}
            </div>
          </>
        ) : (
          <>
            <DatePicker
              label={translate('attendance:holiday.form.date')}
              value={dateValue}
              onChange={handleDateChange}
              disabled={effectiveReadOnly || isSubmitting}
              color={errors.date && isSubmitted ? 'error' : undefined}
              supportingText={
                errors.date?.message && isSubmitted
                  ? translate(String(errors.date.message))
                  : undefined
              }
              placeholder={translate('attendance:holiday.form.datePlaceholder')}
              required
              data-testid="holiday-date-picker"
            />

            <LabelInput
              label={translate('attendance:holiday.form.reason')}
              placeholder={translate('attendance:holiday.form.reasonPlaceholder')}
              value={reasonValue}
              onChange={handleReasonChange}
              required
              disabled={effectiveReadOnly || isSubmitting}
              color={errors.reason && isSubmitted ? 'error' : 'default'}
              supportingText={
                errors.reason?.message && isSubmitted
                  ? translate(String(errors.reason.message))
                  : undefined
              }
              data-testid="holiday-reason-input"
            />

            <Checkbox
              label={translate('attendance:holiday.form.nonWaivable')}
              checked={nonWaivableValue}
              onChange={handleNonWaivableChange}
              disabled={effectiveReadOnly || isSubmitting}
              data-testid="holiday-non-waivable-checkbox"
            />
          </>
        )}
      </SidePanelFormLayout.Fields>

      {!effectiveReadOnly && (
        <SidePanelFormLayout.Footer>
          <PermissionGuard action="create">
            <PrimaryButton
              type="submit"
              fullWidth
              isLoading={isSubmitting}
              disabled={!isReady || isSubmitting}
            >
              {translate('attendance:holiday.form.submit')}
            </PrimaryButton>
          </PermissionGuard>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default CreateHolidayFormPanel;
